/**
 * Per-plan feature bullets for the redesigned plan cards: an icon plus a label.
 *
 * Why this lives in the experiment arm and not in the backend plan catalog: production's card
 * grid reads that same `planFeatures` catalog, so editing those records would change BOTH
 * arms and contaminate the control we are measuring against. Keeping the copy here means the
 * experiment can iterate on wording with no backend change, and if it wins, moving these
 * lists into the catalog becomes part of shipping it.
 *
 * The catalog is still the fallback (see `PlanCardGrid`): a plan with no entry here renders
 * its catalog features behind a plain check mark, so a new tier never shows an empty card.
 *
 * Why the catalog cannot supply these as-is: each plan carries 40-44 feature rows but marks
 * only 6 visible, and the hidden remainder is a shared comparison MATRIX rather than per-tier
 * copy — Standard and Pro Plus list identical hidden entries ("Role Security", "Folder Level
 * Security", "Proof Of Play"…). Unhiding more would repeat the same bullets on every card.
 *
 * `label` doubles as the i18n key (project convention: English text as key) and is read
 * through `t()` from this static map — the sanctioned lookup-map pattern, not dynamic key
 * construction. The icon is a component reference, deliberately NOT part of the translated
 * string: translators cannot break it, and it renders `aria-hidden` so screen readers get the
 * label alone.
 */
import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  Cloud,
  Globe,
  Hand,
  KeyRound,
  LayoutTemplate,
  Link2,
  Monitor,
  MonitorSmartphone,
  Palette,
  Power,
  Puzzle,
  QrCode,
  RefreshCw,
  Rss,
  Tv,
  Users,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface PlanFeatureBullet {
  /** Decorative glyph, rendered aria-hidden. Null falls back to a check mark. */
  icon: LucideIcon | null;
  /** Display text AND i18n key. */
  label: string;
  /**
   * Optional caveat shown behind an info icon next to the label. Also an i18n key.
   * For rows that are true of the plan but not of every device on it.
   */
  tooltip?: string;
}

export const PLAN_FEATURE_COPY: Record<string, readonly PlanFeatureBullet[]> = {
  standard: [
    { icon: Puzzle, label: '100+ Apps & Integrations' },
    { icon: MonitorSmartphone, label: 'Manage Screens Remotely from Web or Mobile' },
    { icon: LayoutTemplate, label: '5,000+ Free Templates' },
    { icon: Rss, label: '140+ Curated Content Feeds' },
    { icon: Users, label: 'Up to 25 Users' },
    { icon: Cloud, label: 'Unlimited Cloud Storage' },
    { icon: Palette, label: 'Designer - Easy-to-Use Content Creation Studio' },
  ],
  proplus: [
    { icon: Link2, label: 'Microsoft 365 & Google Workspace Integration' },
    // Dashboards and secured websites merged into one row — both are "put a protected web
    // page on a screen", and splitting them read as two separate capabilities.
    { icon: BarChart3, label: 'Display Dashboards & Websites' },
    { icon: RefreshCw, label: 'OptiSync - Dynamic Data Mapping' },
    // Hardware-dependent: both need a device that supports the control, so each carries the
    // caveat inline rather than the card implying every screen can do it.
    { icon: Power, label: 'Auto Power On/Off TV', tooltip: 'Applies to select devices' },
    { icon: Wrench, label: 'Remote Troubleshooting', tooltip: 'Applies to select devices' },
    { icon: Building2, label: 'Unlimited Users, Teams, Roles, & Audit Log' },
    { icon: KeyRound, label: 'Single Sign-On with SAML' },
  ],
  engage: [
    { icon: Monitor, label: 'Interactive Kiosk Designer' },
    { icon: BookOpen, label: 'Transform Catalogues into Kiosks' },
    { icon: Hand, label: 'Lift & Learn' },
    { icon: QrCode, label: 'QR Scan-to-Interact' },
    { icon: Tv, label: 'Live TV Ads Overlay' },
    { icon: Activity, label: 'Event Based Analytics' },
    { icon: Globe, label: 'Custom Data Residency Option' },
  ],
};

/**
 * One-line "who is this for" under the plan name, the way most pricing pages orient you
 * before you read any feature list. Kept short on purpose: it has to survive translation on
 * one line inside a narrow card. Also i18n keys, same convention as the labels above.
 */
export const PLAN_TAGLINE: Record<string, string> = {
  standard: 'Essentials to run your screens',
  proplus: 'More control, integrations, and team features',
  engage: 'Interactive kiosks and audience insights',
};

export function getPlanTagline(alias: string | undefined): string | undefined {
  if (!alias) return undefined;
  return PLAN_TAGLINE[alias.toLowerCase()];
}

/** Curated bullets for a plan, or undefined when it has none (caller falls back to catalog). */
export function getPlanFeatureCopy(
  alias: string | undefined,
): readonly PlanFeatureBullet[] | undefined {
  if (!alias) return undefined;
  // Keyed lowercase: the catalog says `proPlus` while `company.payment.planFeature` says
  // `proplus`, so every lookup normalises case (see `isSameAlias`).
  return PLAN_FEATURE_COPY[alias.toLowerCase()];
}

/** Catalog features shaped like curated ones, so callers render one list either way. */
export function toFallbackBullets(descriptions: (string | undefined)[]): PlanFeatureBullet[] {
  return descriptions.filter((d): d is string => !!d).map((label) => ({ icon: null, label }));
}

/**
 * What a downgrade takes away.
 *
 * The lists above are ADDITIVE by tier, the same way the plan cards read them ("Everything in
 * Standard, and:"). So the features lost by moving from a higher tier to a lower one are the
 * union of every tier ABOVE the destination, up to and including the one being left: Engage to
 * Pro Plus loses the `engage` list, Engage to Standard loses `engage` plus `proplus`.
 *
 * Tier order comes from the backend catalog (`usePlanCatalog().orderOf`) rather than a hardcoded
 * ranking here, so a new or reordered tier does not silently produce a wrong list. Returned
 * highest tier first — the most notable loss should be the first thing read.
 *
 * Returns an empty array for anything that is not a downgrade (upgrade, same plan, unknown
 * alias), so the caller can branch on `.length` alone.
 */
export function getFeaturesLostByDowngrade(
  fromAlias: string | undefined,
  toAlias: string | undefined,
  orderOf: (alias: string | undefined | null) => number,
): PlanFeatureBullet[] {
  if (!fromAlias || !toAlias) return [];
  const fromOrder = orderOf(fromAlias);
  const toOrder = orderOf(toAlias);
  if (toOrder >= fromOrder) return [];

  const lostTiers = Object.keys(PLAN_FEATURE_COPY)
    .filter((alias) => orderOf(alias) > toOrder && orderOf(alias) <= fromOrder)
    .sort((a, b) => orderOf(b) - orderOf(a));

  // Dedupe by label: two tiers listing the same capability should not print it twice.
  const seen = new Set<string>();
  return lostTiers
    .flatMap((alias) => PLAN_FEATURE_COPY[alias] ?? [])
    .filter((bullet) => {
      if (seen.has(bullet.label)) return false;
      seen.add(bullet.label);
      return true;
    });
}
